import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { api, ApiError } from "@/lib/api";
import { currentLanguage, LOCALES } from "@/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { goToBank, type BankHandoff } from "@/pages/checkout/bankHandoff";
import BankOptions, { type Bank } from "@/pages/checkout/components/BankOptions";
import CardBrands from "@/pages/checkout/components/CardBrands";
import MerchantOfRecord from "@/components/MerchantOfRecord";
import Footer from "@/pages/home/components/Footer";

interface PaySummary {
  id: string;
  amount: number;
  currency: string;
  description: string | null;
  shopName: string;
  payable: boolean;
  acquirer: Bank | null;
  acquirerName: string | null;
  banks: Record<Bank, boolean>;
  /** Vex deposits only. When set, skip the picker and open that bank. */
  defaultBank: Bank | null;
  /** Vex deposits are not sold by MEDIA-STYLE, so they get no merchant-of-record block. */
  source: "vex" | "plan";
}

/** A server message as received, or the key of one of ours under `pay.errors`. */
type Message = { text: string } | { key: "missingId" | "loadFailed" | "startFailed" };

function formatAmount(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(LOCALES[currentLanguage()], { style: "currency", currency }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

/**
 * Customer-facing pay page used by both Vex (`redirectUrl`) and AIStyle plan
 * checkout. Hosted at `{PUBLIC_PAY_URL}/pay/{id}` (pay.aistyle.com in production).
 */
export default function GatewayPayPage() {
  const { t } = useTranslation();
  const { paymentId } = useParams<{ paymentId: string }>();
  const [summary, setSummary] = useState<PaySummary | null>(null);
  const [loadError, setLoadError] = useState<Message | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Message | null>(null);
  const autoStarted = useRef(false);
  const inFlight = useRef(false);
  const clearBusy = useCallback(() => {
    inFlight.current = false;
    setBusy(false);
  }, []);

  useEffect(() => {
    if (!paymentId) {
      setLoadError({ key: "missingId" });
      return;
    }
    api
      .get<PaySummary>(`/gateway/pay/${encodeURIComponent(paymentId)}`)
      .then(setSummary)
      .catch((err) =>
        setLoadError(err instanceof ApiError ? { text: err.message } : { key: "loadFailed" })
      );
  }, [paymentId]);

  /** Without `bank`, the server picks DSK Bank or PostBank at random. */
  const proceed = useCallback(async (bank?: Bank) => {
    if (!paymentId || inFlight.current) return;
    inFlight.current = true;
    setError(null);
    setBusy(true);
    try {
      const handoff = await api.post<BankHandoff>(
        `/gateway/pay/${encodeURIComponent(paymentId)}/select`,
        bank ? { bank } : {}
      );
      if (!goToBank(handoff, bank)) {
        inFlight.current = false;
        setBusy(false);
      }
    } catch (err) {
      setError(err instanceof Error ? { text: err.message } : { key: "startFailed" });
      inFlight.current = false;
      setBusy(false);
    }
  }, [paymentId]);

  useEffect(() => {
    const bank = summary?.defaultBank;
    if (!summary?.payable || !bank || !summary.banks[bank] || autoStarted.current) return;
    autoStarted.current = true;
    void proceed(bank);
  }, [summary, proceed]);

  useEffect(() => {
    const reset = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      autoStarted.current = false;
      inFlight.current = false;
      setBusy(false);
    };
    window.addEventListener("pageshow", reset);
    return () => window.removeEventListener("pageshow", reset);
  }, []);

  const show = (m: Message) => ("text" in m ? m.text : t(`pay.errors.${m.key}`));

  const unavailableMessage = loadError ? show(loadError) : (summary && !summary.payable
    ? t("pay.linkUnavailable")
    : null);

  return (
    <div className="min-h-screen bg-background-50">
      <main className="max-w-2xl mx-auto px-4 md:px-6 py-10 md:py-16">
        <div className="flex justify-end mb-6">
          <LanguageSwitcher />
        </div>
        {unavailableMessage ? (
          <div className="text-center py-16">
            <h1 className="font-heading font-bold text-xl text-foreground-950">
              {t("pay.unavailableTitle")}
            </h1>
            <p className="mt-2 text-sm text-foreground-600">{unavailableMessage}</p>
          </div>
        ) : !summary ? (
          <div className="flex justify-center py-24">
            <i className="ri-loader-4-line text-3xl text-primary-600 animate-spin"></i>
          </div>
        ) : summary.defaultBank && summary.banks[summary.defaultBank] && !error ? (
          <div className="text-center py-16">
            <i className="ri-loader-4-line text-3xl text-primary-600 animate-spin"></i>
            <p className="mt-4 text-sm text-foreground-600">{t("pay.redirecting")}</p>
            <p className="mt-2 text-foreground-950 font-semibold">
              {formatAmount(summary.amount, summary.currency)}
            </p>
          </div>
        ) : (
          <>
            <p className="text-foreground-600">
              <Trans
                i18nKey="pay.amountToPay"
                values={{ amount: formatAmount(summary.amount, summary.currency) }}
                components={{ amount: <span className="font-semibold text-foreground-950" /> }}
              />
            </p>

            {error && (
              <div className="mt-6 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">
                <i className="ri-error-warning-line mr-1.5"></i>
                {show(error)}
              </div>
            )}

            {summary.source === "plan" && <MerchantOfRecord className="mt-6" />}

            <div className="mt-8">
              <BankOptions
                available={summary.banks}
                busy={busy}
                onSelect={() => void proceed()}
                onRestore={clearBusy}
                unavailableText={t("pay.alreadyStarted")}
              />
            </div>

            <p className="mt-6 text-center text-xs text-foreground-500 flex items-center justify-center gap-1.5">
              <i className="ri-lock-line text-primary-600"></i>
              {t("pay.secureNote")}
            </p>

            <CardBrands secure pci className="mt-4 justify-center" />
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
